/**
 * @fileoverview Reporting router component with access control.
 */

import React from "react";
import {Switch, Redirect, RouteComponentProps} from "react-router-dom";
import {SentryRoute as Route} from "../utils/sentryRoute";

import {
  PageReportingReportsCheckboxes,
  PageReportingFullReport,
  PageReportingDashboards,
  PageReportingGeneral,
  PageReportingOther,
} from "../pages/page-reporting";
import {PageAdmin404} from "../pages/page-admin-404";
import {ConnectedReportContextProvider} from "../pages/page-reporting/ReportContext";
import {AdminHasReportingAccess} from "../models/admin-session/AdminHasReportingAccess";

/**
 * Renderer component for reporting routes.
 * Handles routing for different reporting pages and redirects.
 *
 * @param {RouteComponentProps} props Route component props.
 * @param {Object} props.match Route match object containing path information.
 * @returns {JSX.Element} The reporting router renderer.
 */
export const ReportingRouterRenderer = ({match}: RouteComponentProps) => (
  <ConnectedReportContextProvider>
    <Switch>
      <Route
        exact
        path={`${match.path}`}
        component={() => <Redirect to={`${match.path}/general`} />}
      />
      <Route
        exact
        path={`${match.path}/:type(analytics)/:chartGroup/:chartId/:from1/:to1`}
        component={PageReportingFullReport}
      />
      <Route
        exact
        path={`${match.path}/:type(reports)/:chartGroup/:chartId/:from1/:to1`}
        component={PageReportingFullReport}
      />
      <Route
        exact
        path={`${match.path}/general`}
        component={PageReportingGeneral}
      />
      <Route
        exact
        path={`${match.path}/dashboards`}
        component={PageReportingDashboards}
      />
      <Route
        exact
        path={`${match.path}/checkboxes`}
        component={PageReportingReportsCheckboxes}
      />
      <Route
        exact
        path={`${match.path}/other`}
        component={PageReportingOther}
      />
      <Route exact path={`${match.path}/`} component={PageReportingGeneral} />
      <Route component={PageAdmin404} />
    </Switch>
  </ConnectedReportContextProvider>
);

/**
 * Reporting router with access control.
 * Guards the reporting routes to ensure only users with reporting access can view them.
 */
export const ReportingRouter = AdminHasReportingAccess(ReportingRouterRenderer);
